import { loginRequest } from '@/config/authConfig'
import { msalInstance } from '@/config/msal'
import { GRAPH_SCOPES, TOKEN_STORAGE_KEYS } from '@/constants/msalConstants'
import { useGraphUserDetails } from '@/data-hooks/queries/Graph/useGraphUserDetails'
import { useGraphUserMetadata } from '@/data-hooks/queries/Graph/useGraphUserMetadata'
import { acquireGraphToken, acquireNewToken, getCurrentToken, isTokenExpired, msalLogout } from '@/libs/msalToken'
import { secureStorage } from '@/libs/storage/secureStorage'
import { useAuthStore } from '@/store/auth'
import { InteractionStatus } from '@azure/msal-browser'
import { useAccount, useMsal as useMsalCore } from '@azure/msal-react'
import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'

// Add new type definitions
type TokenType = 'login' | 'graph'

/**
 * useMsalLogin Hook
 *
 * This hook manages the Microsoft Authentication Library (MSAL) authentication flow
 * and Microsoft Graph API integration.
 *
 * Authentication Flow:
 * 1. User initiates login through popup
 * 2. Acquires initial login token with application scopes
 * 3. Acquires separate token for Microsoft Graph API
 * 4. Fetches user details and metadata using Graph API
 * 5. Maintains token refresh cycles
 *
 * Token Management:
 * - Login Token: Used for application authentication
 * - Graph Token: Separate token with User.Read scope for Graph API calls
 * - Both tokens are stored securely with expiration tracking
 *
 * @returns {Object} Authentication methods and state
 * @property {boolean} isAuthenticated - Current authentication state
 * @property {boolean} isLoading - Loading state during auth operations
 * @property {Function} login - Initiates login popup flow
 * @property {Function} logout - Handles user logout
 * @property {Function} refreshToken - Refreshes login token
 * @property {Function} refreshGraphData - Refreshes user data from Graph API
 * @property {Function} isTokenExpired - Checks token expiration status
 */
export const useMsalLogin = () => {
  const { instance, accounts, inProgress } = useMsalCore()
  const account = useAccount(accounts[0] || null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [graphToken, setGraphToken] = useState<string | null>(null)
  const { setUser, setIsAuthenticated: setStoreIsAuthenticated } = useAuthStore()

  const {
    data: userDetails,
    isError: isUserDetailsError,
    refetch: refetchUserDetails,
  } = useGraphUserDetails(graphToken)
  const {
    data: userMetadata,
    isError: isMetadataError,
    refetch: refetchUserMetadata,
  } = useGraphUserMetadata(graphToken)

  /**
   * Sets up axios interceptors for authentication headers
   * Only adds authorization headers for non-authenticated requests
   */
  const setAxiosInterceptors = useCallback((loginToken: string) => {
    axios.interceptors.request.use(
      (config) => {
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${loginToken}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error instanceof Error ? error : new Error(String(error)))
      },
    )
  }, [])

  /**
   * Updates user data in the auth store when received from Graph API
   * Combines user details with metadata (photo)
   */
  useEffect(() => {
    if (!isUserDetailsError && userDetails) {
      setUser({
        id: userDetails.id,
        displayName: userDetails.displayName,
        email: userDetails.mail || userDetails.userPrincipalName,
        jobTitle: userDetails.jobTitle,
        photoUrl: isMetadataError ? null : userMetadata,
        department: userDetails.department,
        officeLocation: userDetails.officeLocation,
        givenName: userDetails.givenName,
      })
    }
  }, [userDetails, userMetadata, isUserDetailsError, isMetadataError, setUser])

  /**
   * Specifically acquires token for Graph API
   * Uses User.Read scope for basic profile access
   */
  const getTokenForGraph = useCallback(async () => {
    try {
      // First check if we already have a valid token
      const existingToken = getCurrentToken('graph')
      if (existingToken) {
        setGraphToken(existingToken)
        return existingToken
      }

      // Otherwise acquire a new token
      const token = await acquireGraphToken(GRAPH_SCOPES.USER_READ)
      if (token) {
        setGraphToken(token)
        return token
      } else {
        console.error('Failed to acquire graph token, got null result')
      }
    } catch (error) {
      console.error('Error acquiring graph token:', error)
    }
    return null
  }, [])

  /**
   * Refreshes user data from Graph API
   * Requires valid graph token
   */
  const refreshGraphData = useCallback(() => {
    if (graphToken) {
      refetchUserDetails()
      refetchUserMetadata()
    }
  }, [graphToken, refetchUserDetails, refetchUserMetadata])

  /**
   * Refreshes the main login token
   * Also triggers Graph token acquisition on success
   */
  const refreshToken = useCallback(async () => {
    if (!account) {
      setIsAuthenticated(false)
      setStoreIsAuthenticated(false)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const token = await acquireNewToken()
      if (token) {
        setAxiosInterceptors(token)
        setIsAuthenticated(true)
        setStoreIsAuthenticated(true)

        // Get graph token separately after login token
        try {
          await getTokenForGraph()
          // After getting both tokens, trigger a refresh of user data
          refreshGraphData()
        } catch (graphError) {
          console.error('Error getting graph token during refresh:', graphError)
        }
      } else {
        throw new Error('Failed to acquire token')
      }
    } catch (error) {
      setIsAuthenticated(false)
      setStoreIsAuthenticated(false)
      console.error('Token refresh failed:', error)
    } finally {
      setIsLoading(false)
    }
  }, [account, getTokenForGraph, setAxiosInterceptors, setStoreIsAuthenticated, refreshGraphData])

  /**
   * Check initial authentication state from token storage
   */
  useEffect(() => {
    const checkInitialAuth = () => {
      // First check for valid token
      const token = getCurrentToken('login')

      // Make sure we have an active account set if there are any accounts
      if (accounts.length > 0 && !instance.getActiveAccount()) {
        instance.setActiveAccount(accounts[0])
      }

      if (token) {
        setIsAuthenticated(true)
        setStoreIsAuthenticated(true)

        // If login token is valid, also get graph token
        const graphToken = getCurrentToken('graph')
        if (graphToken) {
          setGraphToken(graphToken)
        } else {
          // Try to get a fresh graph token
          getTokenForGraph().catch(console.error)
        }

        setIsLoading(false)
      } else if (accounts.length > 0 || instance.getActiveAccount()) {
        // We have an account but token might be expired - will attempt refresh
        setIsLoading(true)
        // Attempt to refresh token immediately
        refreshToken()
      } else {
        setIsAuthenticated(false)
        setStoreIsAuthenticated(false)
        setIsLoading(false)
      }
    }

    checkInitialAuth()

    // Listen for auth events from axios interceptors
    const handleAuthRequired = () => {
      setIsAuthenticated(false)
      setStoreIsAuthenticated(false)
      setIsLoading(false)
    }

    const handleSilentRefreshFailed = () => {
      setIsLoading(false)
    }

    window.addEventListener('auth:required', handleAuthRequired)
    window.addEventListener('auth:silent-refresh-failed', handleSilentRefreshFailed)

    return () => {
      window.removeEventListener('auth:required', handleAuthRequired)
      window.removeEventListener('auth:silent-refresh-failed', handleSilentRefreshFailed)
    }
  }, [instance, accounts, setStoreIsAuthenticated, refreshToken, getTokenForGraph])

  /**
   * Automatically refreshes token when MSAL interaction completes
   */
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      if (account && isTokenExpired('login')) {
        refreshToken()
      } else if (account && !isAuthenticated && getCurrentToken('login')) {
        setIsAuthenticated(true)
        setStoreIsAuthenticated(true)
        setIsLoading(false)
      } else if (!account) {
        setIsLoading(false)
      }
    }
  }, [inProgress, refreshToken, account, isAuthenticated, setStoreIsAuthenticated])

  /**
   * Initiates login flow with popup
   * Triggers token refresh after successful login
   */
  const login = useCallback(() => {
    if (!isLoading) {
      setIsLoading(true)
      msalInstance
        .loginPopup({
          scopes: loginRequest.scopes,
        })
        .then((response) => {
          // Set the account as active immediately after login
          if (response.account) {
            msalInstance.setActiveAccount(response.account)

            // Store tokens directly from the response
            secureStorage.setItem(TOKEN_STORAGE_KEYS.LOGIN_TOKEN, response.idToken)
            const expiresOn = response.expiresOn?.getTime() ?? 0
            secureStorage.setItem(TOKEN_STORAGE_KEYS.LOGIN_EXPIRES, expiresOn.toString())

            // Set auth state directly
            setIsAuthenticated(true)
            setStoreIsAuthenticated(true)

            // Also get the graph token immediately
            getTokenForGraph()
              .then(() => {
                // Now refresh to set up everything else
                refreshToken()
              })
              .catch((error) => {
                console.error('Error getting graph token after login:', error)
                refreshToken()
              })
          } else {
            throw new Error('No account returned from login')
          }
        })
        .catch((error) => {
          console.error('Login failed:', error)
          setIsLoading(false)
        })
    }
  }, [isLoading, refreshToken, getTokenForGraph, setStoreIsAuthenticated])

  /**
   * Handles user logout
   * Redirects to logout page
   */
  const logout = useCallback(() => {
    setIsLoading(true)
    msalLogout().catch((error) => {
      console.error('Logout failed:', error)
      setIsLoading(false)
    })
  }, [])

  return {
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshToken,
    refreshGraphData,
    isTokenExpired: (tokenType: TokenType) => isTokenExpired(tokenType),
  }
}
